/**
 * « / » (and its alias « /discussion »): the conversation with the main agent (V2Conversation, docs/design/APP-APPLE.md
 * §6). Bubbles grouped by author, the agent's character beside its last bubble with its real mood (ecoute while the
 * owner writes, reflechit while a turn runs, parle while its reply streams, joie once something is done), « Ta journée »
 * as the first bubble of today, questions and approvals asked in a bubble with an inset preview and Oui / Non under it,
 * replies to a precise message (the quote inside the reply's bubble, the composer in reply mode), reactions attached to
 * the bubbles (hover actions on a desktop, a long press on a touch screen), and the generated interfaces (chat-ui.tsx)
 * inside the agent's bubbles.
 */
import { Link } from "@tanstack/react-router";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { redactAppPasswords } from "../../shared/app-password";
import { approvalInsetLabel, approvalOutcome, approvalPreview, approvalSentence } from "../../shared/approval-sentence";
import type { ApprovalView, ChatFile, ChatMessage } from "../../shared/contracts";
import { APP_MASK, echoKey, maskShareLinks } from "../../shared/onepassword";
import { findPastedSecrets } from "../../shared/vault";
import { AgentAvatarView, type AvatarAgent, type CharacterMood } from "../components/avatar";
import { ChatUiView } from "../components/chat-ui";
import {
  BUBBLE_TEXT,
  bubbleRadius,
  dayLabel,
  DaySeparator,
  flashLine,
  HoverActions,
  PressMenu,
  PressVeil,
  QuoteInset,
  ReactionBadges,
  ReactionPicker,
  reactionsOf,
  ReplyBar,
  Row,
  sameGroup,
  startOfDay,
  timeLabel,
  TypingDots,
  useDismiss,
  useLongPress,
  useToast,
  type ReactionEmoji,
} from "../components/conversation";
import { DayBrief } from "../components/day-brief";
import { IconArrowUp, IconNote } from "../components/icons";
import { SuggestionChips } from "../components/pulse-summary";
import { RichText } from "../components/rich-text";
import { useShell } from "../components/shell";
import { Button, cx, ErrorNotice, Loading } from "../components/ui";
import { useSettings } from "../lib/agent";
import { api, errorText, fileUrl } from "../lib/api";
import { registerComposer, takeChatHandoff } from "../lib/chat-handoff";
import { channelLabel, fmtBytes, toolLabel } from "../lib/format";
import { clientId, useDesktop, useMediaQuery, useNow, usePageTitle } from "../lib/hooks";
import { flashMood, setListening, useMainMood } from "../lib/mood";
import { usePresence } from "../lib/presence";
import { useAgentEvents, useSocketOpen, useSocketStatus } from "../lib/socket";
import { useResource } from "../lib/store";
import { usePulseSummary } from "./pulse";

const PAGE = 50;
const MAX_LENGTH = 8000;

interface LiveTool {
  name: string;
  status: string;
  summary: string;
}

interface LiveTurn {
  turnId: string;
  /** Unknown when we connected after turn.started. */
  trigger?: string;
  text: string;
  tools: LiveTool[];
  done: boolean;
  /** The final reply already arrived as a chat message (hide the bubble unless more text streams). */
  replied: boolean;
}

interface PendingMessage {
  /** What is sent (the shown text masks an app-specific password: the server keeps it sealed). */
  raw?: string;
  clientId: string;
  text: string;
  status: "sending" | "sent" | "failed";
  error?: string;
  /** Server message ids that existed when this was sent (the echo must be a new one). */
  baseline: Set<string>;
  /** The line this message answers (a reply, a tap on Oui / Non or on an option). */
  replyTo?: string;
  at: number;
}

/** The agent's reply directives ([[tapback:love]], [[reply:k3f9x2]]) never show while its answer streams. */
function withoutDirectives(text: string): string {
  return text.replace(/\[\[\s*(?:tapback|reaction|reply|reponse|réponse)\b[^\]]{0,60}\]\]\s*/giu, "").replace(/\[\[[^\]]{0,40}$/u, "");
}

function upsertMessage(list: ChatMessage[] | undefined, message: ChatMessage): ChatMessage[] {
  const current = list ?? [];
  const i = current.findIndex((m) => m.id === message.id);
  if (i === -1) return [...current, message];
  const copy = current.slice();
  copy[i] = message;
  return copy;
}

/**
 * The text with its secrets (1Password links, pasted passwords or card numbers) as « § »: the server stores the line
 * with placeholders instead (agent/vault), so the echo of a pending bubble is matched on this key.
 */
function secretKey(text: string, stored: boolean): string {
  if (stored) return echoKey(text);
  let t = maskShareLinks(text, "§");
  for (const s of [...findPastedSecrets(t).spans].sort((a, b) => b.start - a.start)) t = `${t.slice(0, s.start)}§${t.slice(s.end)}`;
  return echoKey(t);
}

/** The pending bubble never shows a secret it is about to send (the stored line will hold a placeholder). */
function displayPending(text: string): string {
  let t = maskShareLinks(text, APP_MASK);
  for (const s of [...findPastedSecrets(t).spans].sort((a, b) => b.start - a.start)) t = `${t.slice(0, s.start)}🔐${t.slice(s.end)}`;
  return t;
}

function sameEcho(stored: string, sent: string): boolean {
  if (stored.trim() === sent) return true;
  return stored.includes("🔐") && secretKey(stored, true) === secretKey(sent, false);
}

function unconfirmed(pending: PendingMessage[], messages: ChatMessage[]): PendingMessage[] {
  if (!pending.length) return pending;
  const used = new Set<string>();
  const remaining = pending.filter((p) => {
    if (p.status === "failed") return true;
    const echo = messages.find((m) => m.direction === "in" && m.channel === "web" && !used.has(m.id) && !p.baseline.has(m.id) && sameEcho(m.text, p.text));
    if (!echo) return true;
    used.add(echo.id);
    return false;
  });
  return remaining.length === pending.length ? pending : remaining;
}

function emptyTurn(turnId: string, trigger?: string): LiveTurn {
  return { turnId, trigger, text: "", tools: [], done: false, replied: false };
}

const NO_ANSWERS: readonly string[] = [];

/** Main-conversation triggers whose turn ends with a visible reply in this conversation. */
const REPLY_TRIGGERS = new Set(["user_message", "onboarding", "approval_decision", "question_answer"]);

/** A line as plain words (a quote, the clipboard, a memory). */
function plain(text: string): string {
  return text
    .replace(/\[\[[^\]]{0,60}\]\]/g, "")
    .replace(/\*\*|__|`/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

type Item =
  | { type: "day"; key: string; label: string }
  | { type: "brief"; key: string }
  | { type: "msg"; key: string; m: ChatMessage; first: boolean; last: boolean }
  | { type: "pending"; key: string; p: PendingMessage; first: boolean; last: boolean }
  | { type: "stream"; key: string; t: LiveTurn; first: boolean; last: boolean };

/** The thread as rows: day separators, « Ta journée » first of today, groups of bubbles (reactions are not rows). */
function buildItems(messages: ChatMessage[], pending: PendingMessage[], streaming: LiveTurn[], now: number): Item[] {
  const items: Item[] = [];
  const today = startOfDay(now);
  let lastDay = -1;
  let briefPlaced = false;
  let prev: { out: boolean; at: number } | null = null;
  let prevIndex = -1;
  const day = (at: number) => {
    const d = startOfDay(at);
    if (d === lastDay) return;
    lastDay = d;
    items.push({ type: "day", key: `day-${d}`, label: dayLabel(at, now) });
    prev = null;
    prevIndex = -1;
    if (d === today && !briefPlaced) {
      briefPlaced = true;
      items.push({ type: "brief", key: "brief" });
    }
  };
  const place = (cur: { out: boolean; at: number }, make: (first: boolean) => Item) => {
    const grouped = sameGroup(prev, cur);
    if (grouped && prevIndex >= 0) {
      const p = items[prevIndex]!;
      if (p.type === "msg" || p.type === "pending" || p.type === "stream") p.last = false;
    }
    items.push(make(!grouped));
    prevIndex = items.length - 1;
    prev = cur;
  };
  for (const m of messages) {
    if (m.reaction) continue;
    day(m.createdAt);
    place({ out: m.direction === "out", at: m.createdAt }, (first) => ({ type: "msg", key: m.id, m, first, last: true }));
  }
  for (const p of pending) {
    day(p.at);
    place({ out: false, at: p.at }, (first) => ({ type: "pending", key: p.clientId, p, first, last: true }));
  }
  for (const t of streaming) {
    const at = Math.max(Date.now(), prev ? (prev as { at: number }).at : 0);
    day(at);
    place({ out: true, at }, (first) => ({ type: "stream", key: t.turnId, t, first, last: true }));
  }
  if (!briefPlaced) day(now);
  return items;
}

export function ChatPage() {
  usePageTitle("Discussion");
  const now = useNow(60_000);
  const settings = useSettings();
  const shell = useShell();
  const agentName = settings.data?.agentName ?? "Nova";
  const agent: AvatarAgent = { name: agentName, avatar: settings.data?.avatar ?? null, character: settings.data?.character ?? null, main: true };
  const presence = usePresence();
  const socketStatus = useSocketStatus();
  const liveMood = useMainMood();
  const wide = useMediaQuery("(min-width: 1200px)");
  const desktop = useDesktop();
  const [mountedAt] = useState(() => Date.now());

  const chat = useResource<ChatMessage[]>(
    "chat",
    async (signal) => (await api("GET /api/chat", { query: { limit: String(PAGE) }, signal })).messages,
    // While the realtime socket is down, poll so nothing is missed.
    { refreshOn: ["turn.done"], pollMs: socketStatus === "open" ? false : 5000 },
  );
  // The approvals asked in the conversation: their plain sentence, preview and outcome.
  const approvals = useResource<ApprovalView[]>("approvals:all", async (signal) => (await api("GET /api/approvals", { query: { status: "all" }, signal })).approvals, {
    refreshOn: ["approval.upsert"],
  });
  const approvalById = useMemo(() => new Map((approvals.data ?? []).map((a) => [a.id, a])), [approvals.data]);
  const summary = usePulseSummary();

  const [older, setOlder] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [live, setLive] = useState<Record<string, LiveTurn>>({});
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<{ id: string; who: string; text: string } | null>(null);
  const [picker, setPicker] = useState<string | null>(null);
  const [pressed, setPressed] = useState<string | null>(null);
  const [localReactions, setLocalReactions] = useState<Record<string, string | null>>({});
  const [toast, showToast] = useToast();

  const stick = useRef(true);
  const forceScroll = useRef(true);
  const restore = useRef<{ height: number; y: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerId = useId();

  const messages = useMemo(() => {
    const byId = new Map<string, ChatMessage>();
    for (const m of older) byId.set(m.id, m);
    for (const m of chat.data ?? []) byId.set(m.id, m);
    return [...byId.values()].sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }, [older, chat.data]);

  useAgentEvents((e) => {
    switch (e.type) {
      case "chat.message": {
        chat.mutate((list) => upsertMessage(list, e.message));
        const turnId = e.message.turnId;
        if (e.message.direction === "out" && turnId) {
          setLive((prev) => (prev[turnId] ? { ...prev, [turnId]: { ...prev[turnId], text: "", replied: true } } : prev));
        }
        // The owner's tapback came back from the server: its local copy gives way.
        const target = e.message.reaction && !e.message.reaction.byAgent ? e.message.reaction.targetId : undefined;
        if (target) {
          setLocalReactions((prev) => {
            if (!(target in prev)) return prev;
            const next = { ...prev };
            delete next[target];
            return next;
          });
        }
        break;
      }
      case "turn.started":
        setLive((prev) => ({ ...prev, [e.turnId]: { ...(prev[e.turnId] ?? emptyTurn(e.turnId)), trigger: e.trigger } }));
        break;
      case "turn.delta":
        setLive((prev) => {
          const t = prev[e.turnId] ?? emptyTurn(e.turnId);
          return { ...prev, [e.turnId]: { ...t, text: t.text + e.text, replied: false } };
        });
        break;
      case "turn.tool":
        setLive((prev) => {
          const t = prev[e.turnId] ?? emptyTurn(e.turnId);
          const tools = t.tools.slice();
          const open = tools.findLastIndex((x) => x.name === e.name && x.status === "start");
          if (e.status !== "start" && open !== -1) tools[open] = { name: e.name, status: e.status, summary: e.summary || tools[open]!.summary };
          else if (open !== -1 && e.summary) tools[open] = { ...tools[open]!, summary: e.summary };
          else tools.push({ name: e.name, status: e.status, summary: e.summary });
          return { ...prev, [e.turnId]: { ...t, tools: tools.slice(-8) } };
        });
        break;
      case "turn.done": {
        const turnId = e.turnId;
        setLive((prev) => (prev[turnId] ? { ...prev, [turnId]: { ...prev[turnId], done: true } } : prev));
        // Leave the streamed text on screen until the final message replaces it (or for 1.5 s).
        setTimeout(() => {
          setLive((prev) => {
            if (!prev[turnId]) return prev;
            const next = { ...prev };
            delete next[turnId];
            return next;
          });
        }, 1500);
        break;
      }
      default:
        break;
    }
  });

  // On (re)connection the agent re-sends the running turn's partial text as one turn.delta snapshot.
  useSocketOpen(() => setLive({}));

  // Optimistic bubbles disappear in the same render as their server echo (a new "in" web message with the same text).
  const visiblePending = useMemo(() => unconfirmed(pending, messages), [pending, messages]);
  useEffect(() => {
    if (visiblePending.length !== pending.length) setPending(visiblePending);
  }, [visiblePending, pending]);

  // What was answered to each line (its generated interface checks the option picked): replies sent or on their way.
  const answers = useMemo(() => {
    const byLine = new Map<string, string[]>();
    const add = (lineId: string | undefined, text: string) => {
      if (lineId) byLine.set(lineId, [...(byLine.get(lineId) ?? []), text.trim()]);
    };
    for (const m of messages) if (m.direction === "in" && m.replyTo && !m.reaction) add(m.replyTo.id, m.text);
    for (const p of visiblePending) if (p.status !== "failed") add(p.replyTo, p.text);
    return byLine;
  }, [messages, visiblePending]);

  const reactions = useMemo(() => reactionsOf(messages, localReactions), [messages, localReactions]);

  // The character listens while the owner writes.
  useEffect(() => {
    setListening(focused && draft.trim().length > 0);
  }, [focused, draft]);
  useEffect(() => () => setListening(false), []);

  // Follow the conversation when the reader is at the bottom.
  useEffect(() => {
    const onScroll = () => {
      stick.current = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 160;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const liveSignature = Object.values(live)
    .map((t) => `${t.turnId}:${t.text.length}:${t.tools.length}:${t.done}`)
    .join("|");

  useLayoutEffect(() => {
    if (restore.current) {
      const { height, y } = restore.current;
      restore.current = null;
      window.scrollTo({ top: y + (document.documentElement.scrollHeight - height) });
      return;
    }
    if (!chat.data) return;
    if (stick.current || forceScroll.current) {
      window.scrollTo({ top: document.documentElement.scrollHeight });
      forceScroll.current = false;
      stick.current = true;
    }
  }, [messages.length, pending.length, liveSignature, chat.data]);

  async function loadOlder() {
    const oldest = messages[0];
    if (!oldest || loadingOlder) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const res = await api("GET /api/chat", { query: { before: String(oldest.createdAt), limit: String(PAGE) } });
      restore.current = { height: document.documentElement.scrollHeight, y: window.scrollY };
      setOlder((prev) => [...res.messages, ...prev]);
      if (res.messages.length < PAGE) setHasMore(false);
    } catch (err) {
      setOlderError(errorText(err));
    } finally {
      setLoadingOlder(false);
    }
  }

  async function deliver(item: PendingMessage) {
    try {
      const res = await api("POST /api/chat", { body: { text: item.raw ?? item.text, clientId: item.clientId, ...(item.replyTo ? { replyTo: item.replyTo } : {}) } });
      setPending((prev) => prev.map((p) => (p.clientId === item.clientId ? { ...p, status: "sent" } : p)));
      if (!res.accepted) void chat.reload();
    } catch (err) {
      setPending((prev) => prev.map((p) => (p.clientId === item.clientId ? { ...p, status: "failed", error: errorText(err) } : p)));
    }
  }

  /** Sends `raw`; `answering`: the line a tap answers (Oui / Non, an option), else the reply bar's line if any. */
  function send(raw: string, answering?: string) {
    const text = raw.trim();
    if (!text) return;
    if (text.length > MAX_LENGTH) {
      setComposerError("Trop long : 8 000 caractères au plus.");
      return;
    }
    setComposerError(null);
    const target = answering ?? replyTo?.id;
    // An app-specific password is never shown back: the server masks it in the conversation and keeps it sealed.
    const masked = redactAppPasswords(text).text;
    const item: PendingMessage = {
      clientId: clientId(),
      text: masked,
      ...(masked !== text ? { raw: text } : {}),
      ...(target ? { replyTo: target } : {}),
      status: "sending",
      baseline: new Set(messages.map((m) => m.id)),
      at: Date.now(),
    };
    setPending((prev) => [...prev, item]);
    forceScroll.current = true;
    if (!answering) {
      setDraft("");
      setReplyTo(null);
      if (textareaRef.current) textareaRef.current.style.height = "";
    }
    void deliver(item);
  }

  // Words handed over while the conversation is on screen (a suggestion, an action of « Ta journée »).
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(
    () =>
      registerComposer(({ text, send: now }) => {
        if (now) sendRef.current(text);
        else {
          setDraft(text);
          requestAnimationFrame(() => textareaRef.current?.focus());
        }
      }),
    [],
  );

  // Words handed over from another page: sent once the conversation is loaded (or left in the composer).
  const handedOff = useRef(false);
  useEffect(() => {
    if (handedOff.current || !chat.data) return;
    handedOff.current = true;
    const handoff = takeChatHandoff();
    if (!handoff) return;
    if (handoff.send) send(handoff.text);
    else {
      setDraft(handoff.text);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the conversation first arrives
  }, [chat.data]);

  function retry(item: PendingMessage) {
    const again = { ...item, status: "sending" as const, error: undefined };
    setPending((prev) => prev.map((p) => (p.clientId === item.clientId ? again : p)));
    void deliver(again);
  }

  function startReply(m: { id: string; direction: "in" | "out"; text: string }) {
    setReplyTo({ id: m.id, who: m.direction === "out" ? agentName : "toi", text: plain(m.text) || "Interface" });
    setPicker(null);
    setPressed(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }

  async function react(m: ChatMessage, emoji: ReactionEmoji) {
    const current = reactions.get(m.id)?.me;
    const remove = current === emoji;
    setPicker(null);
    setPressed(null);
    setLocalReactions((prev) => ({ ...prev, [m.id]: remove ? null : emoji }));
    if (!remove) flashMood(emoji === "👎" || emoji === "❓" ? "reflechit" : emoji === "❤️" || emoji === "👍" ? "joie" : "repos", 1200);
    const forget = () =>
      setLocalReactions((prev) => {
        const next = { ...prev };
        delete next[m.id];
        return next;
      });
    try {
      const res = await api("POST /api/chat/:id/react", { params: { id: m.id }, body: { emoji, ...(remove ? { remove: true } : {}) } });
      chat.mutate((list) => upsertMessage(list, res.message));
      forget();
    } catch (err) {
      forget();
      showToast(errorText(err));
    }
  }

  async function copy(m: ChatMessage) {
    setPressed(null);
    try {
      await navigator.clipboard.writeText(plain(m.text));
      showToast("Copié");
    } catch {
      showToast("Pas copié");
    }
  }

  async function remember(m: ChatMessage) {
    setPressed(null);
    try {
      await api("POST /api/memory", { body: { content: plain(m.text).slice(0, 2000), kind: "fact" } });
      showToast("Retenu");
    } catch (err) {
      showToast(errorText(err));
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    send(draft);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(draft);
    } else if (e.key === "Escape" && replyTo) {
      e.preventDefault();
      setReplyTo(null);
    }
  }

  useDismiss(picker !== null, () => setPicker(null));

  // Only main-conversation turns stream text (proactive turns never send deltas): the typing dots while a reply is
  // expected, the reply itself as it streams.
  const liveTurns = Object.values(live);
  const streaming = liveTurns.filter((t) => {
    if (withoutDirectives(t.text)) return true;
    if (t.done || t.replied) return false;
    return t.trigger !== undefined && REPLY_TRIGGERS.has(t.trigger);
  });
  const items = buildItems(messages, visiblePending, streaming, now);

  if (chat.loading) return <Loading />;

  // The newest agent avatar of the thread carries the live mood; the older ones rest.
  let newestAgent = -1;
  items.forEach((it, i) => {
    if ((it.type === "msg" && it.m.direction === "out" && it.last) || (it.type === "stream" && it.last) || it.type === "brief") newestAgent = i;
  });
  const empty = messages.filter((m) => !m.reaction).length === 0 && pending.length === 0 && streaming.length === 0;
  const working = presence.state === "computer";
  const pill = (
    <>
      <AgentAvatarView agent={agent} size={26} bare mood={liveMood} />
      <h1 className="text-[14px] font-semibold">{agentName}</h1>
    </>
  );

  return (
    <div className="flex min-h-[calc(100dvh-96px)] w-full flex-col md:min-h-dvh">
      {/* The floating pill: the agent's character and name (a tap opens its sheet below 1200 px; on a phone the agents
          strip above says who it is). */}
      {desktop ? (
        <div className="pointer-events-none sticky top-0 z-20 flex justify-center pt-3.5">
          <div className="pointer-events-auto flex items-center gap-2 rounded-[20px] bg-elev py-[5px] pr-1.5 pl-1.5">
            {wide ? (
              <span className="flex items-center gap-2 pr-2">{pill}</span>
            ) : (
              <button type="button" onClick={shell?.openAgentSheet} aria-haspopup="dialog" className="flex items-center gap-2 pr-2">
                {pill}
              </button>
            )}
            {working ? (
              <Link to="/ecran" className="flex h-7 items-center gap-1.5 rounded-full bg-chip px-2.5 text-[12.5px] font-semibold">
                <span aria-hidden="true" className="live-dot h-1.5 w-1.5 rounded-full bg-accent" />
                En direct
              </Link>
            ) : null}
          </div>
        </div>
      ) : (
        <h1 className="sr-only">{agentName}</h1>
      )}

      <div className="mx-auto flex w-full max-w-[784px] flex-1 flex-col justify-end px-3 pt-1.5 pb-2.5 md:px-8 md:pt-2">
        {chat.error && !chat.data ? <ErrorNotice message={chat.error.message} onRetry={() => void chat.reload()} /> : null}

        {hasMore && messages.length >= PAGE ? (
          <div className="mb-2 flex flex-col items-center gap-2">
            <Button variant="ghost" size="sm" busy={loadingOlder} onClick={() => void loadOlder()}>
              Messages précédents
            </Button>
            {olderError ? <p className="text-[12.5px] text-danger">{olderError}</p> : null}
          </div>
        ) : null}

        {empty ? (
          <div className="mx-auto mt-6 mb-2 flex flex-col items-center gap-3 text-center">
            <AgentAvatarView agent={agent} size={96} bare mood={liveMood} />
            <p className="font-display text-[24px] leading-snug">Dis bonjour à {agentName}</p>
          </div>
        ) : null}

        <div role="log" aria-label="Conversation" className="flex flex-col pb-2">
          {items.map((it, i) => {
            if (it.type === "day") return <DaySeparator key={it.key} label={it.label} />;
            if (it.type === "brief") {
              return (
                <Row
                  key={it.key}
                  out
                  first
                  last
                  agent={agent}
                  mood={i === newestAgent ? liveMood : "repos"}
                  wide
                  bubble={
                    <div data-bubble className={cx("w-full max-w-[560px] min-w-0 bg-bubble-agent px-3.5 py-3 text-fg", bubbleRadius(true, true))}>
                      <DayBrief />
                    </div>
                  }
                />
              );
            }
            if (it.type === "pending") {
              const p = it.p;
              const quoted = p.replyTo ? messages.find((m) => m.id === p.replyTo) : undefined;
              return (
                <Row
                  key={it.key}
                  out={false}
                  first={it.first}
                  last={it.last}
                  agent={agent}
                  testId="msg-in"
                  fresh
                  bubble={
                    <div
                      data-bubble
                      className={cx("min-w-0 bg-bubble-user whitespace-pre-wrap wrap-anywhere", BUBBLE_TEXT, bubbleRadius(false, it.last), p.status === "sending" ? "text-muted" : "text-fg")}
                      style={{ ["--bb" as string]: "var(--bubble-user)" }}
                    >
                      {quoted ? <QuoteInset who={quoted.direction === "out" ? agentName : "Toi"} text={plain(quoted.text)} onJump={() => flashLine(quoted.id)} /> : null}
                      {displayPending(p.text)}
                    </div>
                  }
                  below={
                    p.status === "failed" ? (
                      <p className="text-right text-[12.5px] text-danger">
                        Pas envoyé{p.error ? ` : ${p.error}` : ""}{" "}
                        <button type="button" className="font-medium underline" onClick={() => retry(p)}>
                          Réessayer
                        </button>{" "}
                        ·{" "}
                        <button type="button" className="font-medium underline" onClick={() => setPending((prev) => prev.filter((x) => x.clientId !== p.clientId))}>
                          Retirer
                        </button>
                      </p>
                    ) : null
                  }
                />
              );
            }
            if (it.type === "stream") {
              const t = it.t;
              const text = withoutDirectives(t.text);
              const tool = [...t.tools].reverse().find((x) => x.status === "start");
              return (
                <Row
                  key={it.key}
                  out
                  first={it.first}
                  last={it.last}
                  agent={agent}
                  mood={text ? "parle" : "reflechit"}
                  testId="msg-streaming"
                  fresh
                  bubble={
                    <div data-bubble aria-busy="true" className={cx("min-w-0 bg-bubble-agent text-fg wrap-anywhere", text ? BUBBLE_TEXT : "px-4 py-3", bubbleRadius(true, it.last))}>
                      {text ? <RichText text={text} /> : <TypingDots label={`${agentName} écrit`} />}
                    </div>
                  }
                  below={!text && tool ? <p className="pl-1 text-[12.5px] text-subtle">{toolLabel(tool.name)}…</p> : null}
                />
              );
            }
            const m = it.m;
            const r = reactions.get(m.id);
            return (
              <MessageRow
                key={it.key}
                m={m}
                first={it.first}
                last={it.last}
                agent={agent}
                agentName={agentName}
                mood={i === newestAgent ? liveMood : "repos"}
                reactionMe={r?.me}
                reactionAgent={r?.agent}
                popReaction={Boolean(r && r.at > mountedAt)}
                fresh={m.createdAt > mountedAt}
                pickerOpen={picker === m.id}
                pressed={pressed === m.id}
                approval={m.ask?.type === "approval" ? approvalById.get(m.ask.id) : undefined}
                answering={visiblePending.some((p) => p.replyTo === m.id)}
                answers={answers.get(m.id) ?? NO_ANSWERS}
                quotedAuthor={m.replyTo ? (m.replyTo.direction === "out" ? agentName : "Toi") : ""}
                onAnswer={(text) => send(text, m.id)}
                onReply={() => startReply(m)}
                onTogglePicker={() => setPicker((p) => (p === m.id ? null : m.id))}
                onPress={() => setPressed(m.id)}
                onClosePress={() => setPressed(null)}
                onReact={(emoji) => void react(m, emoji)}
                onCopy={() => void copy(m)}
                onRemember={() => void remember(m)}
              />
            );
          })}
        </div>
      </div>

      <form onSubmit={onSubmit} className="sticky bottom-0 z-10 bg-bg px-2.5 pt-1.5 pb-[calc(env(safe-area-inset-bottom)+14px)] md:px-8 md:pb-5">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-2">
          {!draft && !replyTo ? <SuggestionChips summary={summary.data} /> : null}
          <div className="flex items-end gap-2">
            {shell ? (
              <button
                type="button"
                aria-label={`${agentName}, réglages`}
                onClick={shell.openAgentSheet}
                className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-elev shadow-[inset_0_0_0_1px_var(--border)] md:hidden"
              >
                <AgentAvatarView agent={agent} size={30} bare mood={liveMood} />
              </button>
            ) : null}
            <div className="min-w-0 flex-1 overflow-hidden rounded-[23px] bg-elev shadow-[inset_0_0_0_1px_var(--border)] md:rounded-3xl">
              {replyTo ? <ReplyBar who={replyTo.who} text={replyTo.text} onCancel={() => setReplyTo(null)} /> : null}
              <div className="flex items-end gap-1.5 py-[3px] pr-[3px] pl-4 md:py-[5px] md:pr-[5px]">
                <label htmlFor={composerId} className="sr-only">
                  Ton message
                </label>
                <textarea
                  id={composerId}
                  ref={textareaRef}
                  value={draft}
                  rows={1}
                  maxLength={MAX_LENGTH + 500}
                  enterKeyHint="send"
                  placeholder={`Écris à ${agentName}`}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    const el = e.target;
                    el.style.height = "auto";
                    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
                  }}
                  onKeyDown={onKeyDown}
                  className="max-h-[150px] min-h-10 min-w-0 flex-1 resize-none bg-transparent py-[9px] text-[16px] leading-[1.4] text-fg outline-none placeholder:text-muted md:text-[15.5px]"
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-ink-fg transition-opacity duration-150 hover:bg-ink/88 disabled:opacity-40 md:h-10 md:w-10"
                >
                  <IconArrowUp width={18} height={18} strokeWidth={2.4} />
                  <span className="sr-only">Envoyer</span>
                </button>
              </div>
            </div>
          </div>
          {composerError ? (
            <p className="px-2 text-[12.5px] text-danger" role="alert">
              {composerError}
            </p>
          ) : draft.length > MAX_LENGTH - 1000 ? (
            <p className="px-2 text-[12px] text-subtle">
              {draft.length} / {MAX_LENGTH}
            </p>
          ) : null}
        </div>
      </form>

      {toast ? (
        <p role="status" className="cv-in fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-full bg-bubble-user px-4 py-2 text-[14px] text-fg shadow-[0_12px_32px_rgb(0_0_0/0.5)]">
          {toast}
        </p>
      ) : null}
    </div>
  );
}

// ───────────── One message ─────────────

function deliveryNote(message: ChatMessage): string | null {
  const values = Object.entries(message.delivery ?? {});
  if (values.some(([, v]) => v === "held")) return "En attente : heures calmes";
  if (values.some(([, v]) => v === "failed")) return "Échec d'envoi";
  return null;
}

/** The deep link of a line (a run, a mission, an approval without its question), as one quiet link. */
function RefLink({ message }: { message: ChatMessage }) {
  const ref = message.ref;
  if (!ref) return null;
  const cls = "mt-2 inline-flex min-h-8 items-center gap-1 text-[13.5px] font-semibold text-muted hover:text-fg max-md:min-h-11";
  switch (ref.type) {
    case "approval":
      return (
        <Link to="/approbations" search={{ focus: ref.id }} className={cls}>
          Voir la validation
        </Link>
      );
    case "run":
      return (
        <Link to="/runs/$id" params={{ id: ref.id }} className={cls}>
          Suivre la mission
        </Link>
      );
    case "mission":
      return (
        <Link to="/missions/$id" params={{ id: ref.id }} className={cls}>
          Voir la mission
        </Link>
      );
    default:
      return null;
  }
}

/** Raster images a browser shows inline (the Worker's file routes serve only these inline); anything else downloads. */
const INLINE_IMAGE = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

/**
 * One file of a chat line (ChatMessage.files): an image inline, any other file as a download link under its own name.
 * The URL is always built from the stored key (session route, owner-checked); a file deleted by the 30-day
 * housekeeping, or an image that no longer loads, becomes a plain line.
 */
function FileItem({ file: f }: { file: ChatFile }) {
  const [broken, setBroken] = useState(false);
  if (f.expired || broken) {
    return (
      <p data-testid="msg-file-expired" className="text-[12.5px] text-subtle wrap-anywhere">
        {f.name} — fichier expiré ou indisponible
      </p>
    );
  }
  const src = fileUrl(f.key);
  if (f.kind === "image" && INLINE_IMAGE.has(f.mime)) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="block max-w-full overflow-hidden rounded-[14px] bg-inset">
        <img data-testid="msg-image" src={src} alt={f.name} loading="lazy" width={f.width} height={f.height} onError={() => setBroken(true)} className="h-auto max-h-80 w-auto max-w-full object-contain" />
      </a>
    );
  }
  return (
    <a
      data-testid="msg-file"
      href={src}
      download={f.name}
      rel="noopener noreferrer"
      className="inline-flex max-w-full items-center gap-2 rounded-[14px] bg-inset px-3 py-2.5 text-[14px] text-fg transition hover:bg-chip"
    >
      <IconNote width={16} height={16} className="shrink-0 text-muted" />
      <span className="min-w-0 truncate">{f.name}</span>
      <span className="shrink-0 text-subtle">· {fmtBytes(f.size)}</span>
    </a>
  );
}

/** The parts of an approval's text in the conversation (shared/risk.ts conversationApprovalMessage). */
const ASK_TAIL = /^(Tu peux aussi me dire|Écris-moi « oui »|You can also say|Just type)/u;
const ASK_LINE = /^([^:\n]{1,40}?) ?: (.*)$/u;
const ASK_HEAD = /\s+—\s+(?:je peux y aller|OK to go)\s*\?\s*$/u;

function parseApprovalText(text: string): { head: string; lines: { label: string; value: string }[] } {
  const [head = "", ...rest] = text.split("\n");
  if (rest.length && ASK_TAIL.test(rest[rest.length - 1]!.trim())) rest.pop();
  const lines: { label: string; value: string }[] = [];
  for (const raw of rest) {
    const m = ASK_LINE.exec(raw);
    if (m) lines.push({ label: m[1]!.trim(), value: m[2]! });
    else if (lines.length) lines[lines.length - 1]!.value += `\n${raw}`;
    else lines.push({ label: "", value: raw });
  }
  return { head: head.replace(ASK_HEAD, "").trim(), lines };
}

/** « Détails »: every exact line of the stored action, folded. */
function AskDetails({ lines }: { lines: { label: string; value: string }[] }) {
  if (!lines.length) return null;
  return (
    <details className="group mt-1.5 text-[13.5px]">
      <summary className="inline-flex min-h-8 cursor-pointer list-none items-center gap-1 text-muted select-none hover:text-fg max-md:min-h-11 [&::-webkit-details-marker]:hidden">
        <span>Détails</span>
        <span aria-hidden="true" className="text-[11px] transition group-open:rotate-90">
          ›
        </span>
      </summary>
      <ul className="mt-1 space-y-1 rounded-[12px] bg-inset p-3 leading-relaxed text-muted">
        {lines.map((l, i) => (
          <li key={`${i}-${l.label}`} className="min-w-0 wrap-anywhere">
            {l.label ? <span className="font-medium">{l.label}</span> : null}
            {l.label ? " : " : null}
            <span className="whitespace-pre-wrap text-fg">{l.value}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** The approval's bubble content (V2 §6.3): one question, an inset preview (to whom, the words or the amount). */
function ApprovalAsk({ text, approval }: { text: string; approval?: ApprovalView }) {
  const parsed = parseApprovalText(text);
  const view = approval ?? { title: parsed.head, lines: parsed.lines, kind: "app_action" as const, status: "pending" as const };
  const sentence = approvalSentence(view);
  const preview = approvalPreview(view);
  const label = approvalInsetLabel(view);
  return (
    <div className="min-w-0">
      <p className="wrap-anywhere">{sentence} ?</p>
      {preview || label ? (
        <div className="mt-2 rounded-[12px] bg-inset px-2.5 py-2">
          {label ? <p className="text-[12.5px] font-semibold text-muted wrap-anywhere">{label}</p> : null}
          {preview?.kind === "quote" ? (
            <blockquote className="mt-0.5 line-clamp-3 text-[14px] leading-snug whitespace-pre-line text-fg wrap-anywhere">{preview.text}</blockquote>
          ) : preview?.kind === "amount" ? (
            <p className="mt-0.5 text-[17px] font-semibold tabular-nums">{preview.text}</p>
          ) : preview?.kind === "warning" ? (
            <p className="mt-0.5 text-[14px] text-danger wrap-anywhere">{preview.text}</p>
          ) : null}
        </div>
      ) : null}
      <AskDetails lines={approval?.lines ?? parsed.lines} />
    </div>
  );
}

const PILL = "inline-flex h-11 max-w-full items-center justify-center rounded-full px-[18px] text-[15px] font-semibold transition md:h-[34px] md:text-[14.5px]";

/** Oui / Non (and « Toujours oui ») under the bubble; a question's choices; one faint word once decided. */
function AskPills({ m, approval, answering, onAnswer }: { m: ChatMessage; approval?: ApprovalView; answering: boolean; onAnswer: (text: string) => void }) {
  const ask = m.ask!;
  if (ask.status !== "pending" || (approval && approval.status !== "pending")) {
    const word =
      ask.type === "question"
        ? "Répondu"
        : !approval
          ? "Décidé"
          : approval.status === "approved" || approval.status === "executed"
            ? "Validé"
            : approval.status === "rejected"
              ? "Refusé"
              : approval.status === "expired"
                ? "Expiré"
                : approval.status === "failed"
                  ? approvalOutcome(approval).replace(/\.$/, "")
                  : "Décidé";
    return <p className="pb-1 pl-1 text-[13px] text-subtle">{word}</p>;
  }
  if (answering) return null;
  const choices = ask.choices?.length ? ask.choices : null;
  const always = ask.type === "approval" && !ask.wordOnly && Boolean(approval?.always || /« ?toujours ?»/i.test(m.text));
  return (
    <div data-testid="msg-ask" className="cv-in flex flex-wrap items-center gap-2 pb-1.5">
      {choices
        ? choices.map((o, i) => (
            <button
              key={`${i}-${o}`}
              type="button"
              onClick={() => onAnswer(o)}
              className={cx(PILL, "min-w-0 truncate", i === 0 ? "bg-ink text-ink-fg hover:bg-ink/88" : "text-fg shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-chip")}
            >
              {o}
            </button>
          ))
        : (ask.wordOnly ? (["Non"] as const) : (["Oui", "Non"] as const)).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => onAnswer(o)}
              className={cx(PILL, o === "Oui" ? "bg-ink text-ink-fg hover:bg-ink/88" : "text-fg shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-chip")}
            >
              {o}
            </button>
          ))}
      {always && !choices ? (
        <button
          type="button"
          title={approval?.always ? `Oui, et ne plus te demander pour : ${approval.always}` : undefined}
          onClick={() => onAnswer("toujours")}
          className="inline-flex h-11 items-center px-1.5 text-[13.5px] text-muted hover:text-fg md:h-[34px]"
        >
          Toujours oui
        </button>
      ) : null}
      {ask.wordOnly ? <span className="px-1 text-[13px] text-subtle">Écris « oui »</span> : null}
    </div>
  );
}

function MessageRow({
  m,
  first,
  last,
  agent,
  agentName,
  mood,
  reactionMe,
  reactionAgent,
  popReaction,
  fresh,
  pickerOpen,
  pressed,
  approval,
  answering,
  answers,
  quotedAuthor,
  onAnswer,
  onReply,
  onTogglePicker,
  onPress,
  onClosePress,
  onReact,
  onCopy,
  onRemember,
}: {
  m: ChatMessage;
  first: boolean;
  last: boolean;
  agent: AvatarAgent;
  agentName: string;
  mood: CharacterMood;
  reactionMe?: string;
  reactionAgent?: string;
  popReaction: boolean;
  fresh: boolean;
  pickerOpen: boolean;
  pressed: boolean;
  approval?: ApprovalView;
  answering: boolean;
  answers: readonly string[];
  quotedAuthor: string;
  onAnswer: (text: string) => void;
  onReply: () => void;
  onTogglePicker: () => void;
  onPress: () => void;
  onClosePress: () => void;
  onReact: (emoji: ReactionEmoji) => void;
  onCopy: () => void;
  onRemember: () => void;
}) {
  const out = m.direction === "out";
  const files = m.files?.length ? m.files : null;
  const ui = out && m.ui ? m.ui : null;
  const hasText = (!files && !ui) || m.text.trim() !== "";
  const longPress = useLongPress(onPress);
  const hasReactions = Boolean(reactionMe || reactionAgent);
  const note = out ? deliveryNote(m) : null;
  const side = out ? "left" : "right";

  let content: ReactNode;
  if (out && m.ask?.type === "approval") content = <ApprovalAsk text={m.text} approval={approval} />;
  else if (out) content = hasText ? <RichText text={m.text} /> : null;
  else content = hasText ? <span className="whitespace-pre-wrap">{m.text}</span> : null;

  const bubble = (
    <div className={cx("relative", ui && "w-full max-w-[560px]")} data-popover>
      <div
        data-bubble
        className={cx(
          "min-w-0 text-fg wrap-anywhere transition-transform duration-200",
          out ? "bg-bubble-agent" : "bg-bubble-user",
          BUBBLE_TEXT,
          bubbleRadius(out, last),
          ui ? "w-full" : "max-w-[560px]",
          pressed && "scale-[1.04] shadow-[0_14px_34px_rgb(0_0_0/0.6)]",
        )}
        style={{ ["--bb" as string]: out ? "var(--bubble-agent)" : "var(--bubble-user)" }}
        onContextMenu={(e) => {
          if (window.matchMedia("(hover: none)").matches) e.preventDefault();
        }}
      >
        {m.replyTo ? <QuoteInset who={quotedAuthor} text={plain(m.replyTo.text)} onJump={() => flashLine(m.replyTo!.id)} /> : null}
        {content}
        {ui ? <ChatUiView spec={ui} answers={answers} onAsk={onAnswer} className={hasText ? "mt-2.5" : undefined} /> : null}
        {files ? (
          <div className={cx("flex flex-col gap-1.5", out ? "items-start" : "items-end", hasText && "mt-2")}>
            {files.map((f) => (
              <FileItem key={f.id} file={f} />
            ))}
          </div>
        ) : null}
        {out && !m.ask ? <RefLink message={m} /> : null}
      </div>
      <ReactionBadges me={reactionMe} agent={reactionAgent} side={out ? "right" : "left"} agentName={agentName} pop={popReaction} />
      {pickerOpen && out ? <ReactionPicker current={reactionMe} side={side} onPick={onReact} /> : null}
      {pressed ? (
        <>
          {out ? <ReactionPicker current={reactionMe} side={side} onPick={onReact} large /> : null}
          <PressMenu side={side} onReply={onReply} onCopy={onCopy} onRemember={out && hasText ? onRemember : undefined} />
        </>
      ) : null}
    </div>
  );

  return (
    <>
      {pressed ? <PressVeil onClose={onClosePress} /> : null}
      <Row
        out={out}
        first={first}
        last={last}
        agent={agent}
        mood={mood}
        testId={out ? "msg-out" : "msg-in"}
        id={`msg-${m.id}`}
        hasReactions={hasReactions}
        fresh={fresh}
        lifted={pressed}
        wide={Boolean(ui)}
        onPointerDown={longPress}
        bubble={bubble}
        aside={
          <HoverActions
            out={out}
            time={m.channel === "web" ? timeLabel(m.createdAt) : `${timeLabel(m.createdAt)} · ${channelLabel(m.channel)}`}
            onReact={out ? onTogglePicker : undefined}
            onReply={onReply}
            pickerOpen={pickerOpen}
          />
        }
        below={
          <>
            {out && m.ask ? <AskPills m={m} approval={approval} answering={answering} onAnswer={onAnswer} /> : null}
            {note ? <p className="pl-1 text-[12.5px] text-subtle">{note}</p> : null}
          </>
        }
      />
    </>
  );
}
